import {
  createAsyncThunk, createSlice, EntityState, PayloadAction,
} from '@reduxjs/toolkit';
import { fetchDefaultProject, fetchProject, fetchProjectList } from 'api/project.service';
import { ProjectResponse } from 'types/entity/project-response';
import { ProjectAndAdditionalInfoResponse } from 'types/entity/project-and-additional-info-response';
import { PositionTypeQuery } from 'types/entity';
import { RootState } from 'redux/store';
import { notificationActions } from 'redux/notification/notification.slice';
import { projectAdapter } from './project-read.adapter';

export const PROJECT_READ_STORE_NAME = 'projectRead';

export const loadDefaultProject = createAsyncThunk<ProjectAndAdditionalInfoResponse, undefined, {state: RootState}>(
  `${PROJECT_READ_STORE_NAME}/loadDefault`,
  async (_, { getState }) => {
    const { positionType } = getState()[PROJECT_READ_STORE_NAME];
    return fetchDefaultProject(positionType);
  },
);

export const loadProject = createAsyncThunk<ProjectResponse, {projectId: string}, {state: RootState}>(
  `${PROJECT_READ_STORE_NAME}/load`,
  async (payload: { projectId: string }, { getState }) => {
    const { projectId } = payload;
    const { positionType } = getState()[PROJECT_READ_STORE_NAME];
    return fetchProject(projectId, positionType);
  },
);

export const loadProjectList = createAsyncThunk<ProjectResponse[], void, {state: RootState}>(
  `${PROJECT_READ_STORE_NAME}/loadProjectList`,
  async (_, thunkAPI) => {
    const { positionType } = thunkAPI.getState()[PROJECT_READ_STORE_NAME];
    const response = await fetchProjectList(positionType);

    // プロジェクト一覧の結果が0件の場合に警告を表示する
    if (response.length === 0) {
      thunkAPI.dispatch(notificationActions.addWarning({ messageId: 'notification.projectRead.warning' }));
    }
    return response;
  },
);

export interface ProjectReadState extends EntityState<ProjectResponse> {
    loaded: boolean
    projectListLoaded:boolean
    selectedId: string
    error: any
    positionType?: PositionTypeQuery
}

export const initialProjectReadState: ProjectReadState = projectAdapter.getInitialState({
  loaded: false,
  projectListLoaded: false,
  selectedId: '',
  error: null,
  positionType: undefined,
});

export const projectReadSlice = createSlice({
  name: PROJECT_READ_STORE_NAME,
  initialState: initialProjectReadState,
  reducers: {
    selectOne: (state, action: PayloadAction<string>) => {
      state.selectedId = action.payload;
      return state;
    },
    setPositionType: (state, action: PayloadAction<PositionTypeQuery | undefined>) => {
      state.positionType = action.payload;
      return state;
    },
  },
  extraReducers: (builder) => {
    // デフォルトプロジェクト取得
    builder.addCase(loadDefaultProject.pending, (state) => {
      state.loaded = false;
    });
    builder.addCase(loadDefaultProject.fulfilled, (state, action) => {
      projectAdapter.upsertOne(state, action.payload);
      state.selectedId = action.payload.project_id || '';
      state.loaded = true;
    });
    builder.addCase(loadDefaultProject.rejected, (state, action) => {
      state.error = action.payload;
      state.loaded = true;
    });
    // プロジェクト取得
    builder.addCase(loadProject.pending, (state) => {
      state.loaded = false;
    });
    builder.addCase(loadProject.fulfilled, (state, action) => {
      projectAdapter.upsertOne(state, action.payload);
      state.selectedId = action.payload.project_id || '';
      state.loaded = true;
    });
    builder.addCase(loadProject.rejected, (state, action) => {
      state.error = action.payload;
      state.loaded = true;
    });
    // プロジェクト一覧取得
    builder.addCase(loadProjectList.pending, (state) => {
      state.projectListLoaded = false;
    });
    builder.addCase(loadProjectList.fulfilled, (state, action) => {
      projectAdapter.addMany(state, action.payload);
      state.projectListLoaded = true;
    });
    builder.addCase(loadProjectList.rejected, (state, action) => {
      state.error = action.payload;
      state.projectListLoaded = true;
    });
  },
});

export const projectReadActions = {
  ...projectReadSlice.actions,
  loadDefaultProject,
  loadProject,
  loadProjectList,
};

export default projectReadSlice.reducer;
